import { useMemo } from 'react'
import { Activity } from 'lucide-react'
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import GlassCard from '../components/GlassCard'
import PeriodPicker from '../components/PeriodPicker'
import MetricToggle from '../components/MetricToggle'
import Skeleton from '../components/Skeleton'

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null
  const point = payload[0]
  return (
    <div
      className="rounded-xl border px-3 py-2 text-xs shadow-sm"
      style={{
        background: 'var(--dash-surface)',
        borderColor: 'var(--dash-border)',
        color: 'var(--dash-text)',
      }}
    >
      <div className="text-[10px]" style={{ color: 'var(--dash-text-muted)' }}>
        {point.payload.name}
      </div>
      <div className="mt-0.5 text-sm font-semibold tabular-nums">
        {(point.value ?? 0).toLocaleString('pt-br')}
      </div>
    </div>
  )
}

export default function UsageChart({
  graph,
  loading,
  period,
  metric,
  total,
  onChangePeriod,
  onChangeMetric,
}) {
  const [startTick, endTick] = useMemo(() => {
    if (!graph || graph.length === 0) return [undefined, undefined]
    return [graph[0]?.name, graph[graph.length - 1]?.name]
  }, [graph])

  const isAllZero = !graph?.length || graph.every((d) => d.total === 0)
  const metricLabel = metric === 'MESSAGES' ? 'mensagens' : 'conversas'

  return (
    <GlassCard className="p-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="flex items-center gap-3">
          <div
            className="flex h-9 w-9 items-center justify-center rounded-xl border"
            style={{
              background: 'var(--dash-icon-bg)',
              borderColor: 'var(--dash-border)',
            }}
          >
            <Activity size={15} style={{ color: 'var(--dash-text-soft)' }} />
          </div>
          <div>
            <h2
              className="text-sm font-semibold"
              style={{ color: 'var(--dash-text)' }}
            >
              Uso ao longo do tempo
            </h2>
            <p
              className="mt-0.5 text-xs"
              style={{ color: 'var(--dash-text-muted)' }}
            >
              <span
                className="font-semibold tabular-nums"
                style={{ color: 'var(--dash-text)' }}
              >
                {total.toLocaleString('pt-br')}
              </span>{' '}
              {metricLabel} no período
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <MetricToggle value={metric} onChange={onChangeMetric} />
          <PeriodPicker value={period} onChange={onChangePeriod} />
        </div>
      </div>

      <div className="mt-6 h-[220px] w-full">
        {loading ? (
          <Skeleton className="h-full w-full" />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={graph}
              margin={{ top: 10, right: 8, bottom: 0, left: 0 }}
            >
              <CartesianGrid
                vertical={false}
                stroke="var(--dash-border)"
                strokeDasharray="3 6"
              />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 10, fill: 'var(--dash-text-muted)' }}
                ticks={startTick && endTick ? [startTick, endTick] : undefined}
                interval="preserveStartEnd"
                axisLine={false}
                tickLine={false}
              />
              <YAxis hide domain={isAllZero ? [-1, 1] : [0, 'auto']} />
              <Tooltip
                cursor={{
                  stroke: 'var(--dash-border-strong)',
                  strokeWidth: 1,
                  strokeDasharray: '4 4',
                }}
                content={<ChartTooltip />}
              />
              {isAllZero && (
                <ReferenceLine
                  y={0}
                  stroke="var(--dash-border-strong)"
                  strokeWidth={1}
                />
              )}
              <Line
                type="monotone"
                dataKey="total"
                stroke="var(--dash-primary)"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, fill: 'var(--dash-primary)' }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </GlassCard>
  )
}
